import { RefObject, useRef } from 'react';
import { PaperPlaneIcon, Tooltip, TOOLTIP_SURFACE } from '@repo/atomic-components';
import { StopResponseButton } from './StopResponseButton';

export interface PrimaryActionButtonProps {
  isLoading: boolean;
  isImprovingPrompt: boolean;
  isFetchingClarifiers: boolean;
  hasClarifierQuestions: boolean;
  isAnyDocumentUploading: boolean;
  isCaseFilesBulkSelecting: boolean;
  chatQuery: string;
  hasFileErrors: boolean;
  onSendMessage: () => void;
  onStopGeneratingResponse?: () => void;
  onMouseEnterSend: () => void;
  onMouseLeaveSend: () => void;
}

export const PrimaryActionButton = ({
  isLoading,
  isAnyDocumentUploading,
  isCaseFilesBulkSelecting,
  isImprovingPrompt,
  isFetchingClarifiers,
  hasClarifierQuestions,
  chatQuery,
  hasFileErrors,
  onSendMessage,
  onStopGeneratingResponse,
  onMouseEnterSend,
  onMouseLeaveSend,
}: PrimaryActionButtonProps) => {
  const isImproving = isImprovingPrompt || isFetchingClarifiers;
  const isChatStreaming = isLoading;
  const buttonRef = useRef<HTMLSpanElement>(null);

  if (isChatStreaming) {
    return <StopResponseButton onStop={onStopGeneratingResponse ?? onSendMessage} />;
  }

  // While improving the prompt the send button is just disabled; only streaming shows Stop.
  const isDisabled = isImproving || chatQuery.length < 3 || hasFileErrors || hasClarifierQuestions;

  return (
    <>
      <span ref={buttonRef} className="inline-flex">
        <button
          type="button"
          disabled={isDisabled || isAnyDocumentUploading || isCaseFilesBulkSelecting}
          onClick={() => onSendMessage()}
          aria-label="Send prompt"
          className="bg-theme-dark-green hover:bg-theme-dark-green focus-visible:ring-theme-dark-green flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-none p-0 focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60"
          onMouseEnter={onMouseEnterSend}
          onMouseLeave={onMouseLeaveSend}
        >
          <PaperPlaneIcon className="text-theme-white" width={16} height={16} />
        </button>
      </span>
      <Tooltip
        target={buttonRef as RefObject<HTMLElement>}
        content="Send prompt"
        position="top"
        className={TOOLTIP_SURFACE}
        baseZIndex={10000}
      />
    </>
  );
};
